import Column from '@components/column'
import React, { Fragment, useRef } from 'react'
import {
  StyleBookingId,
  StyleCircle,
  StyleDivider,
  StyleOtherInfoCard,
  StyleOtherInfoGrid,
  StylePassengerGrid,
  StylePassengerWrapper,
  StylePersonalInfoWrapper,
} from './style'
import Typography from '@components/typography'
import Row from '@components/row'
import Status from '@components/status'
import Svg from '@components/svg'
import { callsvg, malesvg, ticketseatlogo, whatsappsvg } from '@assets/icons'
import Button from '@components/button'
import { Download, Printer } from 'lucide-react'
import theme from '@utils/theme'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { useReactToPrint } from 'react-to-print'

const SuccessAlert = ({ item }) => {
  const printRef = useRef<HTMLDivElement>(null)
  const reactToPrintFn = useReactToPrint({ contentRef: printRef })
  const bookingId = item?.id ?? 'N/A'
  const routeFrom = item?.schedule?.route?.departure?.name ?? 'N/A'
  const routeTo = item?.schedule?.route?.destination?.name ?? 'N/A'
  const departureTime = item?.schedule?.departureTime
    ? new Date(item.date + 'T' + item.schedule.departureTime).toLocaleString()
    : 'N/A'

  const contactName = item?.infos?.contactPerson ?? 'N/A'
  const contactNo = item?.infos?.contactNo ?? 'N/A'
  const passengers = item?.infos?.passengers ?? []
  const seats = item?.bookingSeats?.map((s) => s.seat).join(', ') || 'N/A'
  const seatTypes = item?.bookingSeats?.map((s) => s.type).join(', ') || 'N/A'
  const vehicleName = item?.schedule?.vehicle?.name ?? 'N/A'
  const vehicleNumber = item?.schedule?.vehicle?.number ?? 'N/A'
  const vehicleType = item?.schedule?.vehicle?.type ?? 'N/A'
  const boardingPoint = item?.boardingPoint ?? 'N/A'
  const dropOffPoint = item?.dropOffPoint ?? 'N/A'
  const totalAmount = item?.totalAmount ? Number(item.totalAmount) : 0
  const remainingAmount =
    item?.transaction?.infos?.remainingAmount !== undefined
      ? Number(item.transaction.infos.remainingAmount)
      : null

  let paymentStatus = 'N/A'
  if (remainingAmount !== null) {
    if (remainingAmount === 0) paymentStatus = 'Fully Paid'
    else if (remainingAmount > 0 && remainingAmount < totalAmount)
      paymentStatus = 'Partially Paid'
    else if (remainingAmount >= totalAmount) paymentStatus = 'Unpaid'
  }

  // 📄 Download ticket as PDF
  const handleDownload = async () => {
    if (!printRef.current) return

    const canvas = await html2canvas(printRef.current, {
      scale: 2,
      useCORS: true,
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height],
    })

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height)
    pdf.save(`Booking_${bookingId}.pdf`)
  }

  return (
    <Fragment>
      <div ref={printRef} style={{ padding: '16px 14px' }}>
        <Column gap={10}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px',
            }}
          >
            <img
              src="/favicon1.png"
              alt="meroseat logo"
              width={105}
              height={105}
              style={{ display: 'block' }}
            />
          </div>
          <Typography align="center" color="primary" size={20}>
            Booking Confirmed with Mero seat
          </Typography>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '14px',
              gap: '20px',
            }}
          >
            <StyleBookingId>
              <Typography align="center" color="primary" size={12}>
                Booking ID: #{bookingId}
              </Typography>
            </StyleBookingId>
            <Status item={{ status: paymentStatus }} />
          </div>

          <Typography align="center" color="text100" size={20}>
            {routeFrom} to {routeTo}
          </Typography>
          <Typography align="center" color="text200" size={14}>
            Departure Time: {departureTime}
          </Typography>

          <StylePersonalInfoWrapper>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Row styleRow="gap:8px">
                <StyleCircle width={42} />
                <Column gap={2}>
                  <Typography color="text100" size={16}>
                    {contactName}
                  </Typography>
                  <Typography color="text200" size={12}>
                    {contactNo}
                  </Typography>
                </Column>
              </Row>
              <Row styleRow="gap:16px; align-items:center">
                <a href={`tel:${contactNo}`}>
                  <Svg src={callsvg} />
                </a>
                <a
                  href={`https://wa.me/${contactNo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item?.infos?.isSendToWhatsApp && <Svg src={whatsappsvg} />}
                </a>
              </Row>
            </div>

            <StyleDivider />

            <StylePassengerGrid>
              {passengers.length > 0 ? (
                passengers.map((p, idx) => (
                  <StylePassengerWrapper key={idx}>
                    <Svg src={malesvg} />
                    <Typography color={theme.colors.text100} size={14}>
                      {p?.name ?? 'N/A'}
                    </Typography>
                  </StylePassengerWrapper>
                ))
              ) : (
                <Typography color="text200" size={12}>
                  N/A
                </Typography>
              )}
            </StylePassengerGrid>
          </StylePersonalInfoWrapper>

          <StyleOtherInfoGrid>
            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Number of Passengers:
              </Typography>
              <Typography color="text100" size={14}>
                {passengers.length || 'N/A'}
              </Typography>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Seat Number:
              </Typography>
              <Typography color="text100" size={14}>
                {seats}
              </Typography>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Seat Type:
              </Typography>
              <Typography color="text100" size={14}>
                {seatTypes}
              </Typography>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Vehicle Details:
              </Typography>
              <Column gap={1}>
                <Typography color="text100" size={14}>
                  {vehicleName}
                </Typography>
                <Typography color="text200" size={12}>
                  {vehicleNumber}
                </Typography>
              </Column>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Vehicle Type:
              </Typography>
              <Typography color="text100" size={14}>
                {vehicleType}
              </Typography>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Boarding Point:
              </Typography>
              <Typography color="text100" size={14}>
                {boardingPoint}
              </Typography>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Dropoff Point:
              </Typography>
              <Typography color="text100" size={14}>
                {dropOffPoint}
              </Typography>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Total Amount:
              </Typography>
              <Typography color="text100" size={14}>
                {totalAmount ? `Rs.${totalAmount}` : 'N/A'}
              </Typography>
            </StyleOtherInfoCard>

            <StyleOtherInfoCard>
              <Typography color="text100" size={14}>
                Remaining Amount:
              </Typography>
              <Typography color="text100" size={14}>
                {remainingAmount !== null ? `Rs.${remainingAmount}` : 'N/A'}
              </Typography>
            </StyleOtherInfoCard>
          </StyleOtherInfoGrid>

          <br />
          <StyleDivider />
        </Column>
      </div>

      <Row styleRow="justify-content:center;gap:16px">
        <Button
          styleButton={outline}
          title="Print Ticket"
          icon={<Printer width={20} />}
          onClick={reactToPrintFn}
        />
        <Button
          styleButton="border-radius:12px;padding: 18px 45px;"
          title="Download Ticket"
          icon={<Download width={20} />}
          onClick={handleDownload}
        />
      </Row>
    </Fragment>
  )
}

export default SuccessAlert

const outline = `
background:transparent;
border:1px solid ${theme.colors.primary};
color:${theme.colors.primary};
border-radius:12px;
padding: 18px 45px;
`
